/**
 * Estadísticas API module.
 *
 * Covers BI/analytics endpoints: summary range and per-property occupancy KPIs.
 * All endpoints require `administrador` role on the backend.
 *
 * All functions delegate to `apiFetch`, which handles auth headers,
 * JSON parsing and Ant Design error/success toasts automatically.
 */

import { apiFetch } from "./apiClient";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface StatsRangeParams {
  from?: string;
  to?: string;
  [key: string]: string | undefined;
}

/**
 * KPIs de ocupación por propiedad, calculados noche-por-noche en el backend
 * (igual que el cron mensual). El frontend agrega `nochesOcupadas` y
 * `nochesDisponibles` por bucket (sector / dimensión / global) y calcula la
 * ocupación como razón agrupada `Σ ocupadas / Σ disponibles` (D2). Ver
 * docs/flujo-financiero.md §4.4.
 */
export interface OcupacionKpi {
  idPropiedad: number;
  /** Noches del rango (= días del rango para toda propiedad habilitada). */
  totalNights: number;
  /** Noches con al menos una reserva que cae dentro del rango. */
  nochesOcupadas: number;
  /** Noches bloqueadas y NO ocupadas (la reserva manda sobre el bloqueo). */
  nochesBloqueadas: number;
  /** totalNights − nochesBloqueadas = "días habilitados − bloqueos". */
  nochesDisponibles: number;
}

// ─── estadisticasApi ─────────────────────────────────────────────────────────

export const estadisticasApi = {
  /**
   * GET /estadisticas — summary stats for a date range.
   * Requires `administrador` role.
   */
  getRange: (params?: StatsRangeParams): Promise<any | null> => {
    const qs = buildQs(params);
    return apiFetch(`/estadisticas${qs}`, {
      errorMsg: "Error al obtener estadísticas",
    });
  },

  /**
   * GET /estadisticas/ocupacion-kpis — KPIs de ocupación por propiedad, en
   * vivo, para un rango [from, to] inclusivo (fechas YYYY-MM-DD). Requiere rol
   * `administrador`. Devuelve `[]` ante error (apiFetch nunca lanza).
   */
  getOcupacionKpis: async (from: string, to: string): Promise<OcupacionKpi[]> => {
    const qs = buildQs({ from, to });
    const res = (await apiFetch(`/estadisticas/ocupacion-kpis${qs}`, {
      errorMsg: "Error al obtener la ocupación",
    })) as { success?: boolean; data?: OcupacionKpi[] } | null;
    return res?.data ?? [];
  },
};

// ─── Private helpers ──────────────────────────────────────────────────────────

function buildQs(params?: Record<string, string | undefined>): string {
  if (!params) return "";
  const defined = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined),
  ) as Record<string, string>;
  return Object.keys(defined).length
    ? "?" + new URLSearchParams(defined).toString()
    : "";
}
